import { type ChangeEvent, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CloudUpload, Trash } from 'lucide-react';
import { z } from 'zod';

import { inputStyle } from '@/components/form/inputs/input';
import { Tooltip } from '@/components/ui/tooltip';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import { CreateTaskFiles } from '@/task-manager/api/mutations/create-task-files';
import { DeleteTaskFile } from '@/task-manager/api/mutations/delete-task-file';
import { DownloadTaskFile } from '@/task-manager/api/mutations/download-task-file';
import { ListTaskFiles } from '@/task-manager/api/queries/list-task-files';
import type { TaskDetails } from '@/task-manager/entities/task-details';
import type { TaskFile } from '@/task-manager/entities/task-file';
import { formatMB } from '@/utils/format/bytes';
import { label } from '@/utils/format/label';
import { toastResult } from '@/utils/toast';

export type TaskFilesProps = {
  task: TaskDetails;
};

export function TaskFiles({ task }: TaskFilesProps) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const taskFiles = ListTaskFiles.useQuery(
    { task },
    { refetchInterval: 10_000 },
  );
  const createTaskFiles = CreateTaskFiles.useMutation();

  const form = useForm<TaskFilesInputs>({
    resolver: zodResolver(taskFilesInputsSchema),
  });

  const hint = `Maximum file size: ${CreateTaskFiles.MAX_FILE_SIZE_MB} MB`;
  const error = form.formState.errors.files?.[0]?.message;
  const helperText = error ?? hint;

  const style = inputStyle({
    error: Boolean(error),
  });

  function handleOpenFilePicker() {
    hiddenInputRef.current?.click();
  }

  async function handleFilesChange(e: ChangeEvent<HTMLInputElement>) {
    const filesArray = e.currentTarget.files ? [...e.currentTarget.files] : [];
    form.setValue('files', filesArray);

    const isValid = await form.trigger('files');

    if (isValid) {
      form.handleSubmit(handleFilesUpload)();
    }
  }

  async function handleFilesUpload({ files }: TaskFilesInputs) {
    const creation = createTaskFiles.mutateAsync({ task, files });

    await toastResult(creation, {
      loading: label(
        ['Uploading 1 file', `Uploading ${files.length} files`],
        files.length,
      ),
      error(error) {
        switch (error.type) {
          case 'FILE_TOO_BIG': {
            return `The file "${error.filename}" is too large, please choose a file smaller than ${formatMB(error.limit)}`;
          }

          case 'FILE_INVALID_TYPE': {
            return `We don't support .${error.given} files, yet`;
          }
        }
      },
      exception: 'An unexpected error occurred. Please try again later.',
      success: undefined,
    });
  }

  return (
    <div className="w-auto space-y-4">
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFilesUpload)}>
            <FormField
              control={form.control}
              name="files"
              render={({ field: { value: _value, ...field } }) => (
                <FormItem>
                  <FormControl>
                    <input
                      {...field}
                      ref={hiddenInputRef}
                      type="file"
                      multiple
                      accept={CreateTaskFiles.ALLOWED_FILE_TYPES.join()}
                      className="hidden"
                      onChange={handleFilesChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              disabled={createTaskFiles.isLoading}
              variant="outline"
              onClick={handleOpenFilePicker}
              className="w-full"
            >
              <CloudUpload />
              Add file
            </Button>
            {helperText ? (
              <div className={style.helperText()}>{helperText}</div>
            ) : null}
          </form>
        </Form>
      </div>
      {taskFiles.data && taskFiles.data.files.length > 0 ? (
        <div className="w-full space-y-2">
          {taskFiles.data?.files.map((file) => (
            <TaskFileItem key={file.id} task={task} file={file} />
          )) ?? null}
        </div>
      ) : null}
    </div>
  );
}

export type TaskFileItemProps = { task: TaskDetails; file: TaskFile };

function TaskFileItem({ task, file }: TaskFileItemProps) {
  const deleteTaskFile = DeleteTaskFile.useMutation();
  const downloadTaskFile = DownloadTaskFile.useMutation();
  const [, queryParams] = useQueryParams({
    fileId: {
      key: 'fileId',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  const handleDeleteFile = useCallback(
    function () {
      if (!window.confirm('Do you really want to delete this file ?')) {
        return;
      }

      deleteTaskFile.mutate({ task, file });
    },
    [task, file, deleteTaskFile],
  );

  async function handleFileClick() {
    queryParams.replace({ fileId: file.id });
  }

  return (
    <div className="flex w-full justify-between gap-2 rounded border border-neutral-200 p-1">
      <Tooltip content="Preview" position="bottom" delay>
        <Button
          variant="link"
          className="font-normal"
          disabled={downloadTaskFile.isLoading}
          onClick={handleFileClick}
        >
          {file.name}
        </Button>
      </Tooltip>
      <Button
        variant="ghost"
        disabled={deleteTaskFile.isLoading}
        onClick={handleDeleteFile}
      >
        <Trash />
      </Button>
    </div>
  );
}

const taskFilesInputsSchema = z.object({
  files: z.array(
    z
      .instanceof(File)
      .refine((file) => {
        return !file || file.size <= CreateTaskFiles.MAX_FILE_SIZE;
      }, `File size must be less than ${CreateTaskFiles.MAX_FILE_SIZE_MB} MB`)
      .refine((file) => {
        return CreateTaskFiles.ALLOWED_FILE_TYPES.includes(file.type);
      }, 'File type not supported'),
  ),
});

type TaskFilesInputs = z.infer<typeof taskFilesInputsSchema>;
